import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Autocomplete,
  Chip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import {
  Add,
  Delete,
  Refresh,
  ExpandMore,
  ExpandLess,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../constants/roles';
import { STORE_TYPES, formatStoreTypeLabel } from '../../constants/storeTypes';
import {
  inventoryOptionLabel,
  inventoryPickerOptions,
  getInventoryDisplayExpiryDate
} from '../../utils/partMasterLabel';

const CREATE_ROLES = [ROLES.STORE_INSPECTOR, ROLES.STORE_EXECUTIVE, ROLES.ACCOUNTABLE_MANAGER, ROLES.AM_MRO, ROLES.SUPER_ADMIN];
const APPROVE_ROLES = [ROLES.STORE_INSPECTOR, ROLES.ACCOUNTABLE_MANAGER, ROLES.AM_MRO, ROLES.SUPER_ADMIN];

const APPROVAL_STATUS_META = {
  PENDING_STORE_APPROVAL: { label: 'Pending approval', color: 'warning' },
  APPROVED: { label: 'Approved', color: 'success' },
  REJECTED: { label: 'Rejected', color: 'error' }
};

const emptyLine = () => ({
  inventoryItemId: '',
  partNumber: '',
  partSearch: '',
  quantity: 1,
  maxQty: 0,
  sourceStoreType: '',
  destinationStoreType: ''
});

function isExpiringSoon(item) {
  const expiry = getInventoryDisplayExpiryDate(item);
  if (!expiry) return false;
  const window = new Date();
  window.setDate(window.getDate() + 30);
  return new Date(expiry) <= window;
}

function formatExpiry(item) {
  const expiry = getInventoryDisplayExpiryDate(item);
  return expiry ? new Date(expiry).toLocaleDateString() : '—';
}

const StoreTransferModule = ({ inventoryDomain = 'STORE', prefillItemId = null }) => {
  const storeApi = useMemo(
    () => (path) => `/api/store${inventoryDomain === 'LOAN' ? '/loan' : ''}${path}`,
    [inventoryDomain]
  );
  const { showToast } = useToast();
  const { user } = useAuth();

  const canCreate = CREATE_ROLES.includes(user?.role);
  const canApprove = APPROVE_ROLES.includes(user?.role);

  const [inventory, setInventory] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [transferReason, setTransferReason] = useState('');
  const [items, setItems] = useState([emptyLine()]);
  const [onlyExpiring, setOnlyExpiring] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [rejectDialog, setRejectDialog] = useState({ open: false, transfer: null, reason: '' });
  const [actioningId, setActioningId] = useState('');
  const prefillAppliedRef = useRef(false);

  const inventoryById = useMemo(
    () => new Map(inventory.map((inv) => [String(inv._id), inv])),
    [inventory]
  );

  const fetchInventory = useCallback(async () => {
    try {
      const response = await axios.get(storeApi('/inventory'));
      const rows = Array.isArray(response.data) ? response.data : [];
      setInventory(rows.filter((row) => Number(row.quantity) > 0 && !row.isArchived));
    } catch (error) {
      console.error('Error fetching inventory:', error);
      showToast('Error loading inventory', 'error');
    }
  }, [storeApi, showToast]);

  const fetchTransfers = useCallback(async () => {
    try {
      const response = await axios.get(storeApi('/transfers'));
      setTransfers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching transfers:', error);
      showToast('Error loading transfers', 'error');
    }
  }, [storeApi, showToast]);

  useEffect(() => {
    fetchInventory();
    fetchTransfers();
  }, [fetchInventory, fetchTransfers]);

  useEffect(() => {
    if (!prefillItemId || prefillAppliedRef.current) return;
    const option = inventoryById.get(String(prefillItemId));
    if (!option) return;
    prefillAppliedRef.current = true;
    setItems([{
      ...emptyLine(),
      inventoryItemId: option._id,
      partNumber: option.partNumber,
      partSearch: inventoryOptionLabel(option),
      maxQty: Number(option.quantity) || 0,
      quantity: Number(option.quantity) || 1,
      sourceStoreType: option.storeType || ''
    }]);
  }, [prefillItemId, inventoryById]);

  const sourceInventory = useMemo(() => {
    if (!onlyExpiring) return inventory;
    return inventory.filter(isExpiringSoon);
  }, [inventory, onlyExpiring]);

  const handleAddLine = () => setItems((prev) => [...prev, emptyLine()]);
  const handleRemoveLine = (index) => setItems((prev) => (
    prev.length === 1 ? [emptyLine()] : prev.filter((_, i) => i !== index)
  ));

  const handleSelectInventory = (index, option) => {
    setItems((prev) => prev.map((row, i) => {
      if (i !== index) return row;
      if (!option) {
        return { ...row, inventoryItemId: '', partNumber: '', partSearch: '', maxQty: 0, sourceStoreType: '' };
      }
      return {
        ...row,
        inventoryItemId: option._id,
        partNumber: option.partNumber,
        partSearch: inventoryOptionLabel(option),
        maxQty: Number(option.quantity) || 0,
        quantity: Math.min(Number(row.quantity) || 1, Number(option.quantity) || 1) || 1,
        sourceStoreType: option.storeType || ''
      };
    }));
  };

  const handleLineChange = (index, field, value) => {
    setItems((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const validateAndBuildPayload = () => {
    if (!transferReason.trim()) {
      showToast('Transfer reason is required', 'error');
      return null;
    }
    const validLines = items.filter((row) => row.inventoryItemId);
    if (validLines.length === 0) {
      showToast('Add at least one item to transfer', 'error');
      return null;
    }
    for (const row of validLines) {
      const qty = Number(row.quantity);
      if (!Number.isFinite(qty) || qty <= 0) {
        showToast(`Enter a valid quantity for ${row.partNumber}`, 'error');
        return null;
      }
      if (row.maxQty && qty > row.maxQty) {
        showToast(`Quantity for ${row.partNumber} exceeds available (${row.maxQty})`, 'error');
        return null;
      }
      if (!row.destinationStoreType) {
        showToast(`Select a destination store for ${row.partNumber}`, 'error');
        return null;
      }
      if (row.sourceStoreType && row.sourceStoreType.toUpperCase() === row.destinationStoreType) {
        showToast(`${row.partNumber} is already in the ${formatStoreTypeLabel(row.destinationStoreType)} store`, 'error');
        return null;
      }
    }
    return {
      transferReason: transferReason.trim(),
      items: validLines.map((row) => ({
        inventoryItemId: row.inventoryItemId,
        quantity: Number(row.quantity),
        destinationStoreType: row.destinationStoreType
      }))
    };
  };

  const handleSubmit = async () => {
    const payload = validateAndBuildPayload();
    if (!payload) return;
    try {
      setSubmitting(true);
      await axios.post(storeApi('/transfer'), payload);
      showToast('Store transfer submitted for approval', 'success');
      setTransferReason('');
      setItems([emptyLine()]);
      fetchTransfers();
    } catch (error) {
      console.error('Error submitting transfer:', error);
      showToast(error.response?.data?.message || 'Error submitting transfer', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (transfer) => {
    try {
      setActioningId(transfer._id);
      await axios.post(storeApi(`/transfers/${transfer._id}/approve`));
      showToast('Transfer approved and applied', 'success');
      fetchTransfers();
      fetchInventory();
    } catch (error) {
      console.error('Error approving transfer:', error);
      showToast(error.response?.data?.message || 'Error approving transfer', 'error');
    } finally {
      setActioningId('');
    }
  };

  const handleReject = async () => {
    const transfer = rejectDialog.transfer;
    if (!transfer) return;
    try {
      setActioningId(transfer._id);
      await axios.post(storeApi(`/transfers/${transfer._id}/reject`), {
        reason: rejectDialog.reason.trim() || undefined
      });
      showToast('Transfer rejected', 'success');
      setRejectDialog({ open: false, transfer: null, reason: '' });
      fetchTransfers();
    } catch (error) {
      console.error('Error rejecting transfer:', error);
      showToast(error.response?.data?.message || 'Error rejecting transfer', 'error');
    } finally {
      setActioningId('');
    }
  };

  const toggleRow = (id) => setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));

  const recipientName = (person) => {
    if (!person) return '—';
    const name = `${person.firstName || ''} ${person.lastName || ''}`.trim();
    return name || person.email || '—';
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Move stock between stores (e.g. expired items to Quarantine or Scrape). Each transfer is
        recorded and requires Store Manager / Accountable Manager approval before inventory is moved.
      </Alert>

      {canCreate && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>New Store Transfer</Typography>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                required
                label="Transfer reason"
                placeholder="e.g. Shelf life expired — move to quarantine"
                value={transferReason}
                onChange={(e) => setTransferReason(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={<Checkbox checked={onlyExpiring} onChange={(e) => setOnlyExpiring(e.target.checked)} />}
                label="Only expired / expiring (≤30d)"
              />
            </Grid>
          </Grid>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 280 }}>Item</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>Current store</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>Expiry</TableCell>
                  <TableCell sx={{ minWidth: 110 }}>Available</TableCell>
                  <TableCell sx={{ minWidth: 110 }}>Qty to move</TableCell>
                  <TableCell sx={{ minWidth: 170 }}>Destination store</TableCell>
                  <TableCell align="center">Remove</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row, index) => {
                  const selected = inventoryById.get(String(row.inventoryItemId));
                  const pickerOptions = inventoryPickerOptions(sourceInventory, row.partSearch || '');
                  return (
                    <TableRow key={index}>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <Autocomplete
                          size="small"
                          sx={{ minWidth: 260 }}
                          options={pickerOptions}
                          value={selected || null}
                          inputValue={row.partSearch || ''}
                          onInputChange={(_, value, reason) => {
                            if (reason === 'reset') return;
                            setItems((prev) => prev.map((r, i) => {
                              if (i !== index) return r;
                              const next = { ...r, partSearch: value };
                              const sel = inventoryById.get(String(r.inventoryItemId));
                              if (sel && value !== inventoryOptionLabel(sel)) {
                                next.inventoryItemId = '';
                                next.partNumber = '';
                                next.maxQty = 0;
                                next.sourceStoreType = '';
                              }
                              return next;
                            }));
                          }}
                          onChange={(_, option) => handleSelectInventory(index, option)}
                          filterOptions={(opts) => opts}
                          getOptionLabel={(option) => inventoryOptionLabel(option)}
                          isOptionEqualToValue={(a, b) => String(a?._id) === String(b?._id)}
                          noOptionsText={
                            String(row.partSearch || '').trim()
                              ? 'No matching items'
                              : 'Type a part number to search'
                          }
                          renderInput={(params) => (
                            <TextField {...params} required placeholder="Search part number" />
                          )}
                        />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        {selected ? formatStoreTypeLabel(selected.storeType) : '—'}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        {selected ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="body2">{formatExpiry(selected)}</Typography>
                            {isExpiringSoon(selected) && (
                              <Chip label="Expiring" size="small" color="warning" />
                            )}
                          </Box>
                        ) : '—'}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        {selected ? `${row.maxQty} ${selected.unitMetric || ''}`.trim() : '—'}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <TextField
                          size="small"
                          type="number"
                          value={row.quantity}
                          onChange={(e) => handleLineChange(index, 'quantity', e.target.value)}
                          inputProps={{ min: 0.001, max: row.maxQty || undefined, step: 'any' }}
                          sx={{ width: 100 }}
                          disabled={!row.inventoryItemId}
                        />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <FormControl size="small" fullWidth sx={{ minWidth: 150 }}>
                          <Select
                            value={row.destinationStoreType}
                            displayEmpty
                            onChange={(e) => handleLineChange(index, 'destinationStoreType', e.target.value)}
                            disabled={!row.inventoryItemId}
                          >
                            <MenuItem value="" disabled>Select store</MenuItem>
                            {STORE_TYPES.map((t) => (
                              <MenuItem
                                key={t.value}
                                value={t.value}
                                disabled={row.sourceStoreType && row.sourceStoreType.toUpperCase() === t.value}
                              >
                                {t.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell align="center" sx={{ verticalAlign: 'top' }}>
                        <IconButton size="small" color="error" onClick={() => handleRemoveLine(index)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button startIcon={<Add />} onClick={handleAddLine} variant="outlined">Add item</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Submitting…' : 'Submit for approval'}
            </Button>
          </Box>
        </Paper>
      )}

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Transfer history</Typography>
          <Button startIcon={<Refresh />} onClick={fetchTransfers} size="small">Refresh</Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Date</TableCell>
                <TableCell>Items</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Requested by</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transfers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">No transfers yet</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                transfers.map((transfer) => {
                  const meta = APPROVAL_STATUS_META[transfer.approvalStatus] || { label: transfer.approvalStatus, color: 'default' };
                  const isPending = transfer.approvalStatus === 'PENDING_STORE_APPROVAL';
                  const expanded = !!expandedRows[transfer._id];
                  return (
                    <React.Fragment key={transfer._id}>
                      <TableRow hover>
                        <TableCell>
                          <IconButton size="small" onClick={() => toggleRow(transfer._id)}>
                            {expanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </TableCell>
                        <TableCell>{new Date(transfer.issuedDate).toLocaleDateString()}</TableCell>
                        <TableCell>{transfer.items?.length || 0} item(s)</TableCell>
                        <TableCell sx={{ maxWidth: 240, whiteSpace: 'pre-wrap' }}>{transfer.transferReason || '—'}</TableCell>
                        <TableCell>{recipientName(transfer.issuedBy)}</TableCell>
                        <TableCell><Chip label={meta.label} color={meta.color} size="small" /></TableCell>
                        <TableCell align="center">
                          {isPending && canApprove ? (
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <Tooltip title="Approve & apply">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="success"
                                    disabled={actioningId === transfer._id}
                                    onClick={() => handleApprove(transfer)}
                                  >
                                    <CheckCircle fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Reject">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    disabled={actioningId === transfer._id}
                                    onClick={() => setRejectDialog({ open: true, transfer, reason: '' })}
                                  >
                                    <Cancel fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          ) : '—'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={7} sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}>
                          <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 2 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Part Number</TableCell>
                                    <TableCell>From store</TableCell>
                                    <TableCell>To store</TableCell>
                                    <TableCell align="right">Qty</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(transfer.items || []).map((item, idx) => (
                                    <TableRow key={idx}>
                                      <TableCell sx={{ fontFamily: 'monospace' }}>
                                        {item.inventoryItemId?.partNumber || item.partNumber || '—'}
                                      </TableCell>
                                      <TableCell>{formatStoreTypeLabel(item.storeType)}</TableCell>
                                      <TableCell>{formatStoreTypeLabel(item.destinationStoreType)}</TableCell>
                                      <TableCell align="right">{item.quantity}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                              {transfer.approvalStatus === 'REJECTED' && transfer.amRejectionReason && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                  Rejected: {transfer.amRejectionReason}
                                </Alert>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, transfer: null, reason: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Store Transfer</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Reason (optional)"
            value={rejectDialog.reason}
            onChange={(e) => setRejectDialog((prev) => ({ ...prev, reason: e.target.value }))}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, transfer: null, reason: '' })}>Cancel</Button>
          <Button onClick={handleReject} variant="contained" color="error" disabled={actioningId === rejectDialog.transfer?._id}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StoreTransferModule;
